/**
 * Persistent data store for demo mode.
 * Mimics a simplified Firestore-like API for local development/demo purposes.
 * Data is persisted to localStorage to survive page refreshes.
 */

const STORAGE_KEY = 'pebble_demo_store';

class DemoStore {
  constructor() {
    this.data = this._loadFromStorage();
  }

  /**
   * Load data from localStorage
   */
  _loadFromStorage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to load demo store from localStorage:', error);
    }
    return {};
  }

  /**
   * Save data to localStorage
   */
  _saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch (error) {
      console.warn('Failed to save demo store to localStorage:', error);
    }
  }

  /**
   * Reset all data in the store
   */
  reset() {
    this.data = {};
    this._saveToStorage();
  }

  /**
   * Get a document by collection and document ID
   * @param {string} collection - Collection path (can include subcollections like "events/123/ticketTypes")
   * @param {string} docId - Document ID
   * @returns {object|null} Document data or null if not found
   */
  getDoc(collection, docId) {
    const collectionData = this.data[collection];
    if (!collectionData) return null;
    return collectionData[docId] || null;
  }

  /**
   * Set a document in the store
   * @param {string} collection - Collection path
   * @param {string} docId - Document ID
   * @param {object} data - Document data
   */
  setDoc(collection, docId, data) {
    if (!this.data[collection]) {
      this.data[collection] = {};
    }
    this.data[collection][docId] = { ...data };
    this._saveToStorage();
  }

  /**
   * Update a document (merge with existing data)
   * @param {string} collection - Collection path
   * @param {string} docId - Document ID
   * @param {object} data - Fields to update
   */
  updateDoc(collection, docId, data) {
    if (!this.data[collection]) {
      this.data[collection] = {};
    }
    const existing = this.data[collection][docId] || {};
    this.data[collection][docId] = { ...existing, ...data };
    this._saveToStorage();
  }

  /**
   * Delete a document from the store
   * @param {string} collection - Collection path
   * @param {string} docId - Document ID
   */
  deleteDoc(collection, docId) {
    if (this.data[collection]) {
      delete this.data[collection][docId];
      this._saveToStorage();
    }
  }

  /**
   * Get multiple documents from a collection with optional filters
   * @param {string} collection - Collection path
   * @param {Array} filters - Optional array of filter objects: { type: 'where', field, op, value }
   * @returns {Array} Array of { id, data } objects
   */
  getDocs(collection, filters = []) {
    const collectionData = this.data[collection];
    if (!collectionData) return [];

    let results = Object.entries(collectionData).map(([id, data]) => ({
      id,
      data,
    }));

    // Apply filters
    for (const filter of filters) {
      if (filter.type === 'where') {
        results = results.filter((doc) => {
          const fieldValue = doc.data[filter.field];
          switch (filter.op) {
            case '==':
              return fieldValue === filter.value;
            case '!=':
              return fieldValue !== filter.value;
            case '>':
              return fieldValue > filter.value;
            case '>=':
              return fieldValue >= filter.value;
            case '<':
              return fieldValue < filter.value;
            case '<=':
              return fieldValue <= filter.value;
            case 'in':
              return Array.isArray(filter.value) && filter.value.includes(fieldValue);
            case 'not-in':
              return Array.isArray(filter.value) && !filter.value.includes(fieldValue);
            case 'array-contains':
              return Array.isArray(fieldValue) && fieldValue.includes(filter.value);
            default:
              return true;
          }
        });
      }
    }

    return results;
  }

  /**
   * Query documents using a simple query interface
   * @param {string} collection - Collection path
   * @param {object} queryParams - Query parameters
   * @returns {Array} Array of { id, data } objects
   */
  query(collection, queryParams = {}) {
    const filters = [];

    if (queryParams.where) {
      for (const [field, condition] of Object.entries(queryParams.where)) {
        if (typeof condition === 'object' && condition !== null) {
          for (const [op, value] of Object.entries(condition)) {
            filters.push({ type: 'where', field, op, value });
          }
        } else {
          filters.push({ type: 'where', field, op: '==', value: condition });
        }
      }
    }

    return this.getDocs(collection, filters);
  }
}

// Export a singleton instance
const demoStore = new DemoStore();
export default demoStore;
